import React from "react";
import { BookingRecord } from "../../types";
import { getStoredUser } from "../../services/api/auth";
import {
  ChevronRight,
  CheckCircle,
  Printer,
  User,
  Tag,
  ShieldCheck,
  Wallet,
  ArrowDown,
  TrendingUp,
  Users,
  Percent,
  Banknote,
} from "lucide-react";

interface OrderDetailModalProps {
  booking: BookingRecord | null;
  onClose: () => void;
  onUpdateStatus: (id: string, status: any) => void;
  onDelete: (id: string) => void;
  showProfitAnalysis?: boolean;
}

const StatusBadge = ({ status }: { status: string }) => {
  const styles = {
    PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
    PENDING: "bg-amber-100 text-amber-700 border-amber-200",
    CANCELLED: "bg-rose-100 text-rose-700 border-rose-200",
    COMPLETED: "bg-blue-100 text-blue-700 border-blue-200",
  };
  const s = status as keyof typeof styles;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase border ${
        styles[s] || "bg-gray-100"
      }`}
    >
      {status}
    </span>
  );
};

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  booking,
  onClose,
  onUpdateStatus,
  onDelete,
  showProfitAnalysis = false,
}) => {
  const user = getStoredUser();
  const isStaff = user?.role === "staff";

  if (!booking) return null;

  // Gunakan data langsung dari database (sudah di-transform di orders.ts)
  const grossPrice = booking.grossPrice || 0;
  const voucherDiscount = booking.voucherDiscount || 0;
  const bulkDiscount = booking.bulkDiscount || 0;
  const serviceFee = booking.serviceFee || 0;
  const affiliateFee = booking.affiliateFee || 0;
  const customerPaid = booking.totalPrice || 0;
  const netIncome = booking.netIncome || 0;

  const handlePrintInvoice = () => {
    const invoiceWindow = window.open("", "_blank");
    if (!invoiceWindow) return;

    const statusWatermark = booking.status === "PENDING" ? "PENDING" : "LUNAS";
    const watermarkColor = booking.status === "PENDING" ? "#F59E0B" : "#10B981";

    const html = `
            <html>
                <head>
                    <title>Invoice #${booking.code}</title>
                    <style>
                        @page { size: 105mm 148mm; margin: 0; }
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { 
                            font-family: 'Segoe UI', Arial, sans-serif; 
                            padding: 15px; 
                            color: #1e293b; 
                            line-height: 1.4;
                            width: 105mm;
                            min-height: 148mm;
                            position: relative;
                            font-size: 9px;
                        }
                        .watermark {
                            position: fixed;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%) rotate(-30deg);
                            font-size: 40px;
                            font-weight: 900;
                            color: ${watermarkColor};
                            opacity: 0.15;
                            z-index: 0;
                            pointer-events: none;
                            white-space: nowrap;
                            letter-spacing: 5px;
                        }
                        .content { position: relative; z-index: 1; }
                        .header { 
                            border-bottom: 1.5px solid #e2e8f0; 
                            padding-bottom: 10px; 
                            margin-bottom: 12px; 
                            display: flex; 
                            justify-content: space-between; 
                            align-items: center; 
                        }
                        .logo { font-weight: 800; font-size: 14px; color: #4F46E5; }
                        .info-grid { 
                            display: grid; 
                            grid-template-columns: 1fr 1fr; 
                            gap: 8px; 
                            margin-bottom: 15px; 
                        }
                        .label { 
                            font-size: 7px; 
                            font-weight: 700; 
                            text-transform: uppercase; 
                            color: #64748b; 
                            letter-spacing: 0.5px; 
                            margin-bottom: 2px;
                        }
                        .val { font-weight: 600; font-size: 9px; }
                        table { width: 100%; border-collapse: collapse; margin: 12px 0; }
                        th { 
                            text-align: left; 
                            background: #f8fafc; 
                            padding: 6px 8px; 
                            font-size: 7px; 
                            text-transform: uppercase; 
                            border-bottom: 1.5px solid #e2e8f0;
                            font-weight: 700;
                        }
                        td { padding: 8px; border-bottom: 1px solid #f1f5f9; font-size: 8px; }
                        .total-row { background: #f1f5f9; font-weight: 800; font-size: 11px; }
                        .footer { 
                            margin-top: 15px; 
                            text-align: center; 
                            font-size: 7px; 
                            color: #94a3b8;
                            border-top: 1px dashed #e2e8f0;
                            padding-top: 10px;
                        }
                        .status-badge {
                            display: inline-block;
                            padding: 3px 8px;
                            border-radius: 4px;
                            font-size: 8px;
                            font-weight: 700;
                            background: ${
                              booking.status === "PENDING"
                                ? "#FEF3C7"
                                : "#D1FAE5"
                            };
                            color: ${watermarkColor};
                        }
                        @media print {
                            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                        }
                    </style>
                </head>
                <body>
                    <div class="watermark">${statusWatermark}</div>
                    <div class="content">
                        <div class="header">
                            <div class="logo">GIZ & RIW PARK</div>
                            <div style="text-align: right">
                                <div class="label">INVOICE</div>
                                <div class="val">#${booking.code}</div>
                            </div>
                        </div>
                        <div class="info-grid">
                            <div>
                                <div class="label">CUSTOMER</div>
                                <div class="val">${booking.customerName}</div>
                            </div>
                            <div style="text-align: right">
                                <div class="label">TANGGAL KUNJUNGAN</div>
                                <div class="val">${booking.date}</div>
                            </div>
                            <div>
                                <div class="label">KATEGORI</div>
                                <div class="val">${
                                  booking.category || "Personal"
                                }</div>
                            </div>
                            <div style="text-align: right">
                                <div class="label">STATUS</div>
                                <div class="status-badge">${statusWatermark}</div>
                            </div>
                        </div>
                        <table>
                            <thead><tr><th>Deskripsi</th><th style="text-align: center">Qty</th><th style="text-align: right">Jumlah</th></tr></thead>
                            <tbody>
                                <tr>
                                    <td>${booking.ticketType}</td>
                                    <td style="text-align: center">${
                                      booking.pax
                                    }x</td>
                                    <td style="text-align: right">Rp ${grossPrice.toLocaleString()}</td>
                                </tr>
                                ${
                                  voucherDiscount > 0
                                    ? `<tr><td style="color:#EF4444">Diskon (${
                                        booking.voucherCode
                                      })</td><td style="text-align: center">-</td><td style="text-align: right; color:#EF4444">-Rp ${voucherDiscount.toLocaleString()}</td></tr>`
                                    : ""
                                }
                                ${
                                  bulkDiscount > 0
                                    ? `<tr><td style="color:#EF4444">Diskon Rombongan</td><td style="text-align: center">-</td><td style="text-align: right; color:#EF4444">-Rp ${bulkDiscount.toLocaleString()}</td></tr>`
                                    : ""
                                }
                                <tr class="total-row">
                                    <td colspan="2">TOTAL BAYAR</td>
                                    <td style="text-align: right">Rp ${customerPaid.toLocaleString()}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="footer">
                            <p style="font-weight: 600; margin-bottom: 8px;">Terima kasih telah berkunjung ke GIZ & RIW Park</p>
                            <p>Kebun Binatang Surabaya</p>
                            <div style="margin-top: 10px; padding-top: 8px; border-top: 1px dashed #e2e8f0;">
                                <p>gizrabbitland.com</p>
                                <p style="margin-top: 3px;">WhatsApp: 085385530464</p>
                                <p style="margin-top: 3px;">GIZ: @giz.park | RIW: @rabbitswonderlandsby</p>
                            </div>
                        </div>
                    </div>
                </body>
            </html>
        `;
    invoiceWindow.document.write(html);
    invoiceWindow.document.close();
    setTimeout(() => invoiceWindow.print(), 500);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      ></div>
      <div className="relative w-full max-w-md bg-[#F8FAFC] h-full shadow-2xl flex flex-col animate-slideLeft">
        <div className="p-6 border-b border-gray-100 flex justify-between items-start bg-white">
          <div>
            <h3 className="text-xl font-black text-gray-900 uppercase tracking-tighter">
              Ringkasan Pesanan
            </h3>
            <p className="text-[10px] text-gray-400 font-mono mt-1 tracking-widest">
              {booking.code}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin">
          <div className="flex justify-center flex-col items-center gap-2">
            <StatusBadge status={booking.status} />
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Waktu: {new Date(booking.bookingDate).toLocaleString("id-ID")}
            </p>
          </div>

          {showProfitAnalysis ? (
            <div className="bg-[#121826] text-white p-5 rounded-[2rem] shadow-2xl relative overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-indigo-500/10 p-1.5 rounded-lg border border-indigo-500/20">
                  <User className="w-4 h-4 text-indigo-400" />
                </div>
                <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-indigo-400">
                  Detail Pelanggan
                </h4>
              </div>
              <div className="space-y-4 mb-8">
                <div>
                  <p className="text-lg font-black text-white uppercase tracking-tight">
                    {booking.customerName}
                  </p>
                  <div className="flex flex-col gap-1 mt-2">
                    <p className="text-xs text-slate-400 font-bold">
                      {booking.email}
                    </p>
                    <p className="text-xs text-slate-400 font-bold">
                      {booking.phone}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-tighter">
                    <span>Item Tiket</span>
                    <span className="text-white">
                      {booking.pax}x {booking.ticketType}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-tighter">
                    <span>Harga Normal</span>
                    <span className="text-white">
                      Rp {grossPrice.toLocaleString("id-ID")}
                    </span>
                  </div>
                  {(voucherDiscount > 0 || bulkDiscount > 0) && (
                    <div className="flex justify-between text-xs font-bold text-rose-400 uppercase tracking-tighter">
                      <span>Total Potongan</span>
                      <span>
                        - Rp{" "}
                        {(voucherDiscount + bulkDiscount).toLocaleString(
                          "id-ID"
                        )}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-white/10 my-2 pt-3 flex justify-between items-center">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      Total Dibayar
                    </span>
                    <span className="text-xl font-black text-emerald-400">
                      Rp {customerPaid.toLocaleString("id-ID")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="bg-[#10B981]/10 p-1.5 rounded-lg border border-[#10B981]/20">
                    <ShieldCheck className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#10B981]">
                    Analisis Laba
                  </h4>
                </div>
                <div className="space-y-5">
                  <div className="space-y-4">
                    {voucherDiscount > 0 && (
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-[#EF4444]">
                          <Tag className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-black uppercase tracking-widest">
                            Diskon Voucher{" "}
                            <span className="text-white/40 ml-1">
                              ({booking.voucherCode})
                            </span>
                          </span>
                        </div>
                        <span className="text-sm font-black text-[#EF4444]">
                          - Rp {voucherDiscount.toLocaleString("id-ID")}
                        </span>
                      </div>
                    )}
                    {bulkDiscount > 0 && (
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-pink-400">
                          <Percent className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-black uppercase tracking-widest">
                            Diskon Rombongan{" "}
                            <span className="text-white/40 ml-1">
                              ({booking.category})
                            </span>
                          </span>
                        </div>
                        <span className="text-sm font-black text-pink-400">
                          - Rp {bulkDiscount.toLocaleString("id-ID")}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <div
                        className={`flex items-center gap-2 ${
                          serviceFee > 0 ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-black uppercase tracking-widest">
                          Biaya Layanan (1.4%)
                        </span>
                      </div>
                      <span
                        className={`text-sm font-black ${
                          serviceFee > 0 ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        - Rp {serviceFee.toLocaleString("id-ID")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div
                        className={`flex items-center gap-2 ${
                          affiliateFee > 0 ? "text-[#F59E0B]" : "text-slate-500"
                        }`}
                      >
                        <Users className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-black uppercase tracking-widest">
                          Komisi Mitra (6%){" "}
                          {booking.affiliateName && (
                            <span className="text-white/40 ml-1">
                              ({booking.affiliateName})
                            </span>
                          )}
                        </span>
                      </div>
                      <span
                        className={`text-sm font-black ${
                          affiliateFee > 0 ? "text-[#F59E0B]" : "text-slate-500"
                        }`}
                      >
                        - Rp {affiliateFee.toLocaleString("id-ID")}
                      </span>
                    </div>
                  </div>
                  <div className="pt-6 mt-2 border-t border-white/10 flex justify-between items-center">
                    <div className="space-y-1">
                      <span className="text-[10px] font-black text-[#10B981] uppercase tracking-[0.2em] block">
                        Laba Bersih Akhir
                      </span>
                      <span className="text-3xl font-black text-white tracking-tighter">
                        Rp {netIncome.toLocaleString("id-ID")}
                      </span>
                    </div>
                    <div className="bg-[#10B981]/20 p-3 rounded-2xl border border-[#10B981]/30">
                      <TrendingUp className="w-6 h-6 text-[#10B981]" />
                    </div>
                  </div>
                </div>
              </div>
              <Wallet className="absolute -top-6 -right-6 w-32 h-32 text-white opacity-[0.03] rotate-12" />
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-[2rem] p-7 space-y-6 shadow-sm">
              <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                <div className="bg-indigo-50 p-2 rounded-xl text-indigo-600">
                  <User className="w-4 h-4" />
                </div>
                <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Detail Pelanggan
                </h4>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-lg font-black text-gray-900 uppercase tracking-tight">
                    {booking.customerName}
                  </p>
                  <div className="flex flex-col gap-1 mt-2">
                    <p className="text-xs text-gray-400 font-bold uppercase">
                      {booking.email}
                    </p>
                    <p className="text-xs text-gray-400 font-bold uppercase">
                      {booking.phone}
                    </p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <div className="flex items-center gap-2 mb-4 text-emerald-600">
                    <Banknote className="w-4 h-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest">
                      Rincian Pembayaran
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs font-bold text-gray-400 uppercase tracking-tighter">
                      <span>Item Tiket</span>
                      <span className="text-gray-900">
                        {booking.pax}x {booking.ticketType}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs font-bold text-gray-400 uppercase tracking-tighter">
                      <span>Harga Normal</span>
                      <span className="text-gray-900">
                        Rp {grossPrice.toLocaleString("id-ID")}
                      </span>
                    </div>
                    {(voucherDiscount > 0 || bulkDiscount > 0) && (
                      <div className="flex justify-between text-xs font-bold text-rose-500 uppercase tracking-tighter">
                        <span>Total Potongan</span>
                        <span>
                          - Rp{" "}
                          {(voucherDiscount + bulkDiscount).toLocaleString(
                            "id-ID"
                          )}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 my-2 pt-3 flex justify-between items-center">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Total Dibayar
                      </span>
                      <span className="text-xl font-black text-emerald-600">
                        Rp {customerPaid.toLocaleString("id-ID")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100 bg-white space-y-3">
          {booking.status === "PENDING" && !isStaff && (
            <button
              onClick={() => onUpdateStatus(booking.id, "PAID")}
              className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4" /> Konfirmasi Lunas
            </button>
          )}
          <div className="flex gap-3">
            <button
              onClick={handlePrintInvoice}
              className="flex-1 py-4 border-2 border-gray-100 text-gray-700 font-black uppercase tracking-widest text-[10px] rounded-2xl flex items-center justify-center gap-2"
            >
              <Printer className="w-4 h-4" /> Cetak Invoice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
